// App.jsx
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import RouteScrollToTop from "./helper/RouteScrollToTop";

// === Auth wrappers ===
import { AuthProvider } from "./auth/AuthProvider.jsx";
import { PartnerAuthProvider } from "./auth/PartnerAuthProvider.jsx";
import RequireAuth from "./auth/RequireAuth.jsx";
import RequireSchool from "./auth/RequireSchool.jsx";
import RequireSuperAdmin from "./auth/RequireSuperAdmin.jsx";
import { ToastProvider } from "./components/Toast.jsx";

// === Pages ===
import HomePageOne from "./pages/HomePageOne";
import InviteDriverPage from "./pages/InviteDriverPage";
import ErrorPage from "./pages/ErrorPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import InviteSuccessPage from "./pages/InviteSuccessPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";

import DriverRoutesPage from "./pages/DriverRoutesPage";
import LiveBusMapPage from "./pages/LiveBusMapPage";
import DriverListPage from "./pages/DriverListPage";
import SchoolQRPage from "./pages/SchoolQRPage";
import SchoolsPage from "./pages/SchoolsPage";
import SuperAdminUsersPage from "./pages/SuperAdminUsersPage";
import SuperAdminSubscriptionsPage from "./pages/SuperAdminSubscriptionsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

// === Partner Pages ===
import PartnerRegisterPage from "./pages/partner/PartnerRegisterPage";
import PartnerSignInPage from "./pages/partner/PartnerSignInPage";
import PartnerDashboardPage from "./pages/partner/PartnerDashboardPage";

// Helper to wrap a protected element (login + school required)
const secure = (el) => (
  <RequireAuth>
    <RequireSchool>{el}</RequireSchool>
  </RequireAuth>
);

// Helper to wrap super admin only routes
const superAdminOnly = (el) => (
  <RequireAuth>
    <RequireSuperAdmin>{el}</RequireSuperAdmin>
  </RequireAuth>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <RouteScrollToTop />
        <Routes>
          {/* ===== PUBLIC (ONLY auth pages) ===== */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/invite-success" element={<InviteSuccessPage />} />
          <Route path="/school/:schoolId" element={<SchoolQRPage />} />

          
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* ===== SALES PARTNER ROUTES ===== */}
          <Route path="/partner" element={<Navigate to="/partner/sign-in" replace />} />
          <Route path="/partners" element={<Navigate to="/partner/sign-in" replace />} />
          <Route path="/partner/register" element={<PartnerAuthProvider><PartnerRegisterPage /></PartnerAuthProvider>} />
          <Route path="/partner/sign-in" element={<PartnerAuthProvider><PartnerSignInPage /></PartnerAuthProvider>} />
          <Route path="/partner/login" element={<Navigate to="/partner/sign-in" replace />} />
          <Route path="/partner/dashboard" element={<PartnerAuthProvider><PartnerDashboardPage /></PartnerAuthProvider>} />

          {/* ===== PROTECTED ROOT (login + school required) ===== */}
          <Route path="/" element={secure(<HomePageOne />)} />
          <Route path="/schools" element={secure(<SchoolsPage />)} />
          <Route path="/invite-driver" element={secure(<InviteDriverPage />)} />
          <Route path="/driver-list" element={secure(<DriverListPage />)} />
          <Route path="/routes" element={secure(<DriverRoutesPage />)} />
          <Route path="/live-map" element={secure(<LiveBusMapPage />)} />

          {/* ===== SUPER ADMIN ONLY ===== */}
          <Route path="/admin/users" element={superAdminOnly(<SuperAdminUsersPage />)} />
          <Route path="/admin/subscriptions" element={superAdminOnly(<SuperAdminSubscriptionsPage />)} />

          {/* Create Company route - redirect to home since companies are auto-created */}
          <Route path="/create-company" element={<Navigate to="/" replace />} />

          {/* 404 */}
          <Route path="*" element={<ErrorPage />} />
        </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
